import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import {
  getLicensePlateContents,
  type LicensePlateContentDto,
  type LicensePlateDto,
} from './api';
import { LicensePlateStatusBadge } from './LicensePlateStatusBadge';

interface Props {
  plate: LicensePlateDto | null;
  onClose: () => void;
}

/**
 * What is on a plate, and where it is. The read-only half of the legacy License Plate Inquiry
 * screen (sp_WebInquiry_LicensePlateGet / _GetInfo), with two differences worth knowing about:
 *
 *   * Legacy's inquiry takes a @DomainID and then never uses it — it groups by DomainID but
 *     filters only on the plate number, so it shows contents from every warehouse at once. Here
 *     the warehouse is on every row.
 *   * Quantities are per line, in each item's own stocking unit. There is no total, because adding
 *     EA to LBS to FT produces a number that means nothing.
 */
export function LicensePlateDetailModal({ plate, onClose }: Props) {
  const { t } = useTranslation();

  const contents = useQuery({
    queryKey: ['license-plate-contents', plate?.licensePlateId] as const,
    queryFn: ({ signal }) => getLicensePlateContents(plate!.licensePlateId, signal),
    enabled: plate !== null,
  });

  const rows = contents.data ?? [];

  /**
   * What is actually sitting on the pallet. The item is the headline and the quantity the aside,
   * because "how much of what" is the question somebody opens this to answer; the location matters
   * on a plate that turned out to be split across two of them.
   */
  const columns: DataColumn<LicensePlateContentDto>[] = useMemo(
    () => [
      {
        key: 'item',
        label: t('licensePlates.item'),
        card: 'title',
        render: (r) => (
          <Stack gap={0}>
            <Code>{r.itemNumber}</Code>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {r.itemDescription}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'quantity',
        label: t('licensePlates.quantity'),
        align: 'right',
        card: 'aside',
        // Each line in its own unit — see the component note on why there is no total row.
        render: (r) => (
          <Text size="sm" fw={500}>
            {r.quantity.toLocaleString(undefined, { maximumFractionDigits: 5 })}{' '}
            <Text span size="xs" c="dimmed">
              {r.uomCode}
            </Text>
          </Text>
        ),
      },
      {
        key: 'location',
        label: t('licensePlates.location'),
        render: (r) => (
          <Stack gap={0}>
            <Text size="sm">{r.locationCode}</Text>
            <Text size="xs" c="dimmed">
              {r.warehouseCode}
            </Text>
          </Stack>
        ),
      },
      {
        key: 'lot',
        label: t('licensePlates.lot'),
        render: (r) => r.lotNumber ?? '—',
      },
      {
        key: 'bucket',
        label: t('licensePlates.bucket'),
        // On-hand is the ordinary case, so only the others earn a badge.
        render: (r) =>
          r.balanceTypeId === 1 ? (
            <Text size="sm" c="dimmed">
              {r.balanceTypeName}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="orange">
              {r.balanceTypeName}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <AppModal
      opened={plate !== null}
      onClose={onClose}
      width="lg"
      title={
        <Group gap="sm" wrap="nowrap">
          <Text fw={600}>{plate?.licensePlateNumber ?? ''}</Text>
          {plate && <LicensePlateStatusBadge status={plate.status} />}
        </Group>
      }
    >
      <Stack gap="md">
        <Group gap="xl" wrap="wrap">
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('licensePlates.whereIsIt')}
            </Text>
            {/* Three distinct states, and the count is what separates them. An empty plate and a
                split one both have no single location, but for opposite reasons. */}
            {plate && plate.locationCount === 0 && (
              <Text size="sm" c="dimmed">
                {t('licensePlates.nowhere')}
              </Text>
            )}
            {plate && plate.locationCount === 1 && (
              <Text size="sm">
                <Code>{plate.locationCode}</Code>{' '}
                <Text span c="dimmed">
                  {plate.warehouseCode}
                </Text>
              </Text>
            )}
            {plate && plate.locationCount > 1 && (
              <Text size="sm">{t('licensePlates.severalPlaces', { places: plate.locationCount })}</Text>
            )}
          </Stack>

          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              {t('licensePlates.itemsOnPlate')}
            </Text>
            <Text size="sm">{plate?.itemCount ?? 0}</Text>
          </Stack>

          {plate?.closedAtUtc && (
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {t('licensePlates.retiredOn')}
              </Text>
              <Text size="sm">{new Date(plate.closedAtUtc).toLocaleString()}</Text>
            </Stack>
          )}
        </Group>

        {/* Split across locations is not an error, but it is worth flagging: it usually means a
            partial move, and it is the state legacy silently resolved by choosing one location. */}
        {plate && plate.locationCount > 1 && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
            title={t('licensePlates.splitTitle')}
          >
            {t('licensePlates.splitBody')}
          </Alert>
        )}

        {contents.isLoading && (
          <Center py="lg">
            <Loader color="invetrak" size="sm" />
          </Center>
        )}

        {contents.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('licensePlates.errors.contentsFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(contents.error as Error).message}</Text>
              {contents.error instanceof ApiError && contents.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{contents.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void contents.refetch()}>
                  {t('licensePlates.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {contents.isSuccess && rows.length === 0 && (
          <Text size="sm" c="dimmed">
            {t('licensePlates.emptyPlate')}
          </Text>
        )}

        {contents.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(r) =>
              `${r.locationId}-${r.itemId}-${r.lotId ?? 'nolot'}-${r.balanceTypeId}`
            }
            minWidth={640}
            highlightOnHover
            verticalSpacing="xs"
            horizontalSpacing="md"
          />
        )}
      </Stack>
    </AppModal>
  );
}
